import cn from 'classnames';
import Icon from 'components/Icon';
import { DOTS, usePagination } from './usePagination';

import styles from './Pagination.module.scss';

interface PaginationProps {
  onPageChange: (page: number) => void;
  totalCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize: number;
  showShowingData?: boolean;
  className?: string;
}

export const Pagination = (props: PaginationProps) => {
  const {
    onPageChange,
    totalCount,
    siblingCount = 1,
    currentPage,
    pageSize,
    showShowingData = false,
    className,
  } = props;

  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  if (!paginationRange) return <></>;

  // If there are less than 2 times in pagination range we shall not render the component
  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <div
      className={cn(className, {
        [styles.paginationWrapper]: showShowingData,
      })}
    >
      {showShowingData ? (
        <p>
          Showing {(currentPage - 1) * 10 + 1}-
          {currentPage * 10 > totalCount ? totalCount : currentPage * 10} of{' '}
          {totalCount}
        </p>
      ) : null}
      <ul className={styles.paginationContainer}>
        {/* Left navigation arrow */}
        <li
          className={cn(styles.paginationItem, {
            [styles.disabled]: currentPage === 1,
          })}
          onClick={() => {
            currentPage !== 1 && onPrevious();
          }}
        >
          <Icon iconName="icon_left-arrow-2" size="small" />
        </li>
        {paginationRange.map((pageNumber, index) => {
          // If the pageItem is a DOT, render the DOTS unicode character
          if (pageNumber === DOTS) {
            return (
              <li
                key={`dots-${index}`}
                className={cn(styles.paginationItem, styles.dots)}
              >
                &#8230;
              </li>
            );
          }

          // Render our Page Pills
          return (
            <li
              key={pageNumber}
              className={cn(styles.paginationItem, {
                [styles.selected]: pageNumber === currentPage,
              })}
              onClick={() => onPageChange(pageNumber as number)}
            >
              {pageNumber}
            </li>
          );
        })}
        {/*  Right Navigation arrow */}
        <li
          className={cn(styles.paginationItem, {
            [styles.disabled]: currentPage === lastPage,
          })}
          onClick={() => {
            currentPage !== lastPage && onNext();
          }}
        >
          <Icon iconName="icon_right-arrow" size="small" />
        </li>
      </ul>
    </div>
  );
};
